import { useEffect, useState, useMemo } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Download, Trash2, PackageMinus, X, Pencil, Printer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FormModal, Field, SectionTitle, StockTypeTabs } from "@/pages/InwardStock";
import { useCustomFields, CustomFieldsRenderer } from "@/components/CustomFields";

const DISPATCH_TYPES = ["Sale", "Dispatch", "Warranty Replacement", "Sample"];
const PAYMENT_MODES = ["Cash", "Bank Transfer", "UPI", "Credit", "Cheque"];

export default function OutwardStock() {
  const { user } = useAuth();
  const [stockType, setStockType] = useState("ready");
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm("ready"));
  const { fields: customFields } = useCustomFields("outward");

  function initialForm(st) {
    return {
      stock_type: st || "ready",
      batch_number: "",
      customer_id: "",
      dispatch_date: new Date().toISOString().slice(0, 10),
      dispatch_type: "Sale",
      quantity: 1,
      unit_price: 0,
      payment_mode: "Cash",
      invoice_number: "",
      serial_number: "",
      notes: "",
      custom_data: {},
      location_id: "",
    };
  }

  const load = async () => {
    const [o, c, s, l] = await Promise.all([
      api.get(`/outward?stock_type=${stockType}`),
      api.get("/customers"),
      api.get(`/stock/in-hand?stock_type=${stockType}`),
      api.get("/locations"),
    ]);
    setItems(o.data);
    setCustomers(c.data);
    setBatches(s.data);
    setLocations(l.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stockType]);

  const selectedBatch = useMemo(() => batches.find((b) => b.batch_number === form.batch_number), [batches, form.batch_number]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        stock_type: form.stock_type || stockType,
        quantity: parseInt(form.quantity),
        unit_price: parseFloat(form.unit_price),
      };
      if (editingId) {
        await api.patch(`/outward/${editingId}`, payload);
        toast.success("Outward record updated");
      } else {
        await api.post("/outward", payload);
        toast.success("Outward stock recorded");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(initialForm(stockType));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      stock_type: it.stock_type || "ready",
      batch_number: it.batch_number,
      customer_id: it.customer_id,
      dispatch_date: it.dispatch_date,
      dispatch_type: it.dispatch_type,
      quantity: it.quantity,
      unit_price: it.unit_price,
      payment_mode: it.payment_mode,
      invoice_number: it.invoice_number || "",
      serial_number: it.serial_number || "",
      notes: it.notes || "",
      custom_data: it.custom_data || {},
      location_id: it.location_id || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(initialForm(stockType));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this outward record?")) return;
    try {
      await api.delete(`/outward/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const printInvoice = (it) => {
    const gstPercent = 18; // Standard GST for batteries
    const base = (it.total_price || 0) / (1 + gstPercent / 100);
    const gst = (it.total_price || 0) - base;
    const invNo = it.invoice_number || `INV/${(it.dispatch_date || "").replace(/-/g, "")}/${String(it.id).slice(0, 6).toUpperCase()}`;
    const html = `<!doctype html><html><head><title>Invoice ${invNo}</title>
<style>body{font-family:Inter,Arial,sans-serif;padding:40px;color:#18231D}h1{color:#28B516;margin:0}
.head{display:flex;justify-content:space-between;border-bottom:3px solid #28B516;padding-bottom:16px;margin-bottom:24px}
.box{border:1px solid #DCE6DF;background:#F4F7F5;padding:14px 16px;border-radius:10px;margin:12px 0}
table{width:100%;border-collapse:collapse;margin-top:16px}
th,td{border:1px solid #DCE6DF;padding:12px;text-align:left}th{background:#F2F7F3;color:#536057;font-size:12px;text-transform:uppercase;letter-spacing:.08em}
.right{text-align:right}.total{text-align:right;font-size:22px;font-weight:800;color:#28B516;margin-top:16px}
.mini{color:#6B786F;font-size:12px}.sig{margin-top:60px;border-top:1px solid #DCE6DF;padding-top:8px;width:220px;font-size:12px;color:#536057}
.badge{display:inline-block;padding:4px 10px;border-radius:14px;font-size:11px;font-weight:800;background:#E4F7E5;color:#197C28}
</style></head><body>
<div class="head">
  <div><h1>GREENRGY POWER</h1><div class="mini">Lithium Battery Management</div><div class="mini">GSTIN: XXAAAAA0000A0Z0</div></div>
  <div class="right"><h2 style="margin:0">TAX INVOICE</h2><div style="font-size:20px;font-weight:800;color:#28B516">${invNo}</div>
  <div class="mini">Date: ${it.dispatch_date || ""}</div>
  <div style="margin-top:8px"><span class="badge">${it.dispatch_type || "Sale"}</span></div></div>
</div>
<div class="box"><b>Bill To:</b> ${it.customer_name || "—"}${it.linked_so_number ? `<br><span class="mini">Ref SO: ${it.linked_so_number}</span>` : ""}</div>
<table>
<thead><tr><th>Description</th><th>Batch</th><th class="right">Qty</th><th class="right">Unit ₹</th><th class="right">Amount ₹</th></tr></thead>
<tbody>
<tr><td>Lithium Battery — Batch dispatch${it.serial_number ? ` (SN: ${it.serial_number})` : ""}</td><td>${it.batch_number}</td>
<td class="right">${it.quantity}</td><td class="right">${(it.unit_price || 0).toLocaleString()}</td>
<td class="right"><b>${(it.total_price || 0).toLocaleString()}</b></td></tr>
</tbody>
</table>
<div class="box" style="width:340px;margin-left:auto;background:#fff;border-color:#DCE6DF">
<div style="display:flex;justify-content:space-between;padding:2px 0"><span>Taxable Value</span><b>₹${base.toFixed(2)}</b></div>
<div style="display:flex;justify-content:space-between;padding:2px 0"><span>CGST @ ${gstPercent / 2}%</span><b>₹${(gst / 2).toFixed(2)}</b></div>
<div style="display:flex;justify-content:space-between;padding:2px 0"><span>SGST @ ${gstPercent / 2}%</span><b>₹${(gst / 2).toFixed(2)}</b></div>
<div style="display:flex;justify-content:space-between;padding:6px 0;border-top:1px solid #DCE6DF;margin-top:4px"><span><b>Grand Total</b></span><b style="color:#28B516">₹${(it.total_price || 0).toLocaleString()}</b></div>
</div>
<div class="mini" style="margin-top:14px"><b>Payment Mode:</b> ${it.payment_mode || "—"}${it.notes ? `<br><b>Notes:</b> ${it.notes.replace(/</g, "&lt;")}` : ""}</div>
<div class="sig">Authorized Signatory</div>
<script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  const totalRevenue = items.reduce((s, i) => s + (i.total_price || 0), 0);
  const label = stockType === "raw" ? "Raw Material" : "Ready Stock";

  return (
    <div className="p-8 space-y-6" data-testid="outward-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#16A34A] mr-2 align-middle" />Sales / Dispatch · {label}</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Outward Stock</h1>
          <div className="font-mono text-xs text-zinc-500 mt-2">{items.length} records · ₹{totalRevenue.toLocaleString()} total revenue</div>
        </div>
        <div className="flex gap-3">
          <a href={`${API}/export/outward?stock_type=${stockType}`} className="btn-ghost flex items-center gap-2" data-testid="export-outward-btn"><Download className="w-4 h-4" /> Export CSV</a>
          <button onClick={() => { setForm(initialForm(stockType)); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="new-outward-btn"><Plus className="w-4 h-4" /> New Outward</button>
        </div>
      </div>

      <StockTypeTabs value={stockType} onChange={setStockType} testidPrefix="outward" />

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="data-table" data-testid="outward-table">
          <thead>
            <tr>
              <th>Batch #</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Type</th>
              <th>Serial</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit ₹</th>
              <th className="text-right">Total ₹</th>
              <th>Payment</th>
              <th>Invoice</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="11" className="text-center py-12 text-zinc-500 font-sans">
                <PackageMinus className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No outward records. Click <span className="text-[#16A34A] font-semibold">New Outward</span> to dispatch.
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id}>
                <td className="text-[#16A34A] font-semibold">{it.batch_number}</td>
                <td>{it.dispatch_date}</td>
                <td className="font-sans">{it.customer_name}</td>
                <td className="font-sans">{it.dispatch_type}</td>
                <td>{it.serial_number || "—"}</td>
                <td className="text-right">{it.quantity}</td>
                <td className="text-right">{it.unit_price.toLocaleString()}</td>
                <td className="text-right text-[#16A34A] font-semibold">{it.total_price.toLocaleString()}</td>
                <td className="font-sans">{it.payment_mode}</td>
                <td>{it.invoice_number || "—"}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => printInvoice(it)} className="text-zinc-400 hover:text-[#1769AA] transition-colors" data-testid={`invoice-outward-${it.id}`} title="Print Invoice (GST)">
                      <Printer className="w-4 h-4" />
                    </button>
                    <button onClick={() => startEdit(it)} className="text-zinc-400 hover:text-[#16A34A] transition-colors" data-testid={`edit-outward-${it.id}`} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {user?.role === "admin" && (
                      <button onClick={() => remove(it.id)} className="text-zinc-400 hover:text-[#DC2626] transition-colors" data-testid={`del-outward-${it.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <FormModal onClose={closeForm}>
          <form onSubmit={submit} className="space-y-5" data-testid="outward-form">
            <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
              <h2 className="font-display text-2xl uppercase text-zinc-900">{editingId ? "Edit Outward Entry" : "New Outward Entry"}</h2>
              <button type="button" onClick={closeForm} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>

            <SectionTitle>Stock Type</SectionTitle>
            <div className="flex gap-2" data-testid="out-stock-type">
              {[{ k: "ready", label: "Ready Stock" }, { k: "raw", label: "Raw Material" }].map((o) => (
                <button
                  type="button"
                  key={o.k}
                  onClick={() => setForm({ ...form, stock_type: o.k })}
                  data-testid={`out-stock-type-${o.k}`}
                  className={`px-4 py-2 border text-xs uppercase font-bold tracking-widest transition-colors ${
                    form.stock_type === o.k ? "bg-[#16A34A] border-[#16A34A] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-[#16A34A] hover:text-[#16A34A]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            <SectionTitle>Batch & Customer</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Batch Number*">
                <select required value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} className="input-field font-mono" data-testid="out-batch">
                  <option value="">Select batch…</option>
                  {batches.filter((b) => b.in_hand > 0).map((b) => (
                    <option key={b.batch_number} value={b.batch_number}>
                      {b.batch_number} — {b.brand} {b.model} (available: {b.in_hand})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Customer*">
                <select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className="input-field" data-testid="out-customer">
                  <option value="">Select customer…</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Dispatch Date*"><input required type="date" value={form.dispatch_date} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} className="input-field" data-testid="out-date" /></Field>
              <Field label="Dispatch Type">
                <select value={form.dispatch_type} onChange={(e) => setForm({ ...form, dispatch_type: e.target.value })} className="input-field" data-testid="out-dtype">
                  {DISPATCH_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Invoice #"><input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="input-field" data-testid="out-invoice" /></Field>
              <Field label="Serial Number"><input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="input-field font-mono" data-testid="out-serial" /></Field>
              <Field label="Dispatch From (Location)">
                <select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className="input-field" data-testid="out-location">
                  <option value="">— Any location —</option>
                  {locations.filter((l) => l.is_active !== false).map((l) => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
                </select>
              </Field>
            </div>

            {selectedBatch && (
              <div className="border border-[#16A34A]/40 bg-[#16A34A]/5 p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
                <div><div className="overline mb-1">Brand</div>{selectedBatch.brand}</div>
                <div><div className="overline mb-1">Model</div>{selectedBatch.model}</div>
                <div><div className="overline mb-1">Spec</div>{selectedBatch.voltage}V · {selectedBatch.capacity_ah}Ah</div>
                <div><div className="overline mb-1">Available</div><span className="text-[#16A34A] text-base font-bold">{selectedBatch.in_hand}</span></div>
              </div>
            )}

            <SectionTitle>Pricing & Payment</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Quantity*"><input required type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="input-field font-mono" data-testid="out-qty" /></Field>
              <Field label="Unit Price (₹)*"><input required type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="input-field font-mono" data-testid="out-price" /></Field>
              <Field label="Total (auto)">
                <div className="input-field font-mono text-[#16A34A] font-bold">₹{(parseFloat(form.unit_price || 0) * parseInt(form.quantity || 0)).toLocaleString()}</div>
              </Field>
              <Field label="Payment Mode">
                <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="input-field" data-testid="out-payment">
                  {PAYMENT_MODES.map((m) => <option key={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Notes" full><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field h-20 resize-none" data-testid="out-notes" /></Field>
            </div>

            {customFields.length > 0 && (
              <>
                <SectionTitle>Custom Fields</SectionTitle>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <CustomFieldsRenderer
                    fields={customFields}
                    values={form.custom_data}
                    onChange={(cd) => setForm({ ...form, custom_data: cd })}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={closeForm} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-outward">{editingId ? "Update Outward" : "Record Outward"}</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
}
